import { NodeData } from '@candulabs/craft';
import isEqual from 'lodash/isEqualWith';
import { useRef, useState, useEffect, useCallback } from 'react';

import { useEditor } from 'craft/hooks/useEditor';

export interface NodeDataWithId extends NodeData {
  id: string;
}

/**
 * useNodesData
 * Hook to get data for multiple nodes, closely follows useMultiNodes but does not consolidate common properties
 * @param nodeIds An array of nodeIds get data for
 */
export function useNodesData(nodeIds: string[]): NodeDataWithId[] {
  const nodeIdsRef = useRef(nodeIds);

  const { query, store } = useEditor();

  const collectNodes = useCallback(() => {
    const { current: currentNodes } = nodeIdsRef;

    const nodes = currentNodes
      .map((id) => {
        const node = query.node(id).get();

        if (!node) {
          return null;
        }

        const { data } = node;
        return { ...data, id };
      })
      .filter(Boolean);
    return nodes as NodeDataWithId[];
  }, []);

  const nodesRef = useRef<any>();
  const [nodes, setNodes] = useState(() => collectNodes());

  const { subscribe } = store;

  // Collect states on state change
  useEffect(() => {
    const unsubscribe = subscribe((collected) => {
      const nodesData = Object.keys(collected.nodes).map((key) => {
        const node = collected.nodes[key];

        return { id: key, ...node.data };
      });

      if (!isEqual(nodesData, nodesRef.current)) {
        setNodes(collectNodes());
        nodesRef.current = nodesData;
      }
    });

    return () => {
      unsubscribe();
    };
  }, [nodeIds]);

  if (!isEqual(nodeIdsRef.current, nodeIds)) {
    nodeIdsRef.current = nodeIds;
    setNodes(collectNodes());
  }
  return nodes;
}

export default useNodesData;
